/**
 * TileBuildingUI.jsx - Building construction UI for tiles
 */

import React from 'react';
import BuildingChip from '../../common/BuildingChip';
import ResourceChip from '../../common/ResourceChip';
import { canAfford } from '../../../modules/shared';
import { hexDistance, idToCoords } from '../../../utils/hexGrid';

/**
 * TileBuildingButton - Compact button that opens the full building modal
 * Note: Tutorial is triggered by the parent (WorldMapPanel) before opening modal
 */
export function TileBuildingButton({
  availableBuildings,
  onOpenModal,
}) {
  const buildingCount = availableBuildings?.length || 0;

  return (
    <div className="wm-action-group wm-build-section">
      <div className="wm-action-label">🏗️ Build (2×)</div>
      <button
        className="wm-open-build-modal-btn"
        onClick={() => onOpenModal?.()}
        disabled={buildingCount === 0}
      >
        <span className="wm-build-modal-icon">🔨</span>
        <span className="wm-build-modal-text">
          {buildingCount > 0 ? `Select Building (${buildingCount})` : 'No Buildings Available'}
        </span>
      </button>
    </div>
  );
}

/**
 * TileBuildingUI - Legacy dropdown UI (kept for backwards compatibility)
 */
export function TileBuildingUI({
  availableBuildings,
  selectedBuilding,
  setSelectedBuilding,
  buildingCounts,
  playerResources,
  getTileBuildCost,
  onBuild,
  triggerTutorial
}) {
  const checkAfford = (cost) => canAfford(cost, playerResources);

  return (
    <div className="wm-action-group wm-build-section">
      <div className="wm-action-label">🏗️ Build (2x)</div>
      {availableBuildings.length > 0 ? (
        <div className="wm-building-list">
          {availableBuildings.slice(0, 10).map(bld => {
            const cost = getTileBuildCost?.(bld.id) || {};
            const affordable = canAfford(cost, playerResources);
            const costCount = Object.keys(cost).length;
            return (
              <div
                key={bld.id}
                className={`wm-building-row ${selectedBuilding === bld.id ? 'selected' : ''} ${!affordable ? 'unaffordable' : ''}`}
                onClick={() => {
                  triggerTutorial?.('firstTileBuild');
                  setSelectedBuilding(selectedBuilding === bld.id ? null : bld.id);
                }}
                onDoubleClick={() => {
                  if (affordable) {
                    triggerTutorial?.('firstTileBuild');
                    onBuild(bld.id);
                  }
                }}
                style={{ flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}
              >
                <div className="wm-building-row-top">
                  <BuildingChip id={bld.id} size="small" count={buildingCounts?.[bld.id] || 0} fullWidth />
                </div>
                <div className={`wm-building-row-bottom count-${costCount}`}>
                  {Object.entries(cost).map(([r, a]) => (
                    <ResourceChip
                      key={r}
                      resourceId={r}
                      amount={a}
                      currentAmount={playerResources[r] || 0}
                      canAfford={(playerResources[r] || 0) >= a}
                      size="small"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="wm-building-empty">No buildings</div>
      )}
      {selectedBuilding && (
        <div className="wm-build-action">
          <button
            className="wm-build-btn"
            onClick={() => onBuild(selectedBuilding)}
            disabled={!checkAfford(getTileBuildCost?.(selectedBuilding))}
          >
            Build
          </button>
        </div>
      )}
    </div>
  );
}

export function BuiltTileInfo({
  currentNodeData,
  // Trade Route props
  nodeId,
  canBuildRoute,
  routeCost,
  isOnTradeRoute,
  onBuildRoute,
  playerResources,
  triggerTutorial,
  startGuidedTour,
  completedGuidedTours,
}) {
  if (!currentNodeData?.hasTileBuilding || !currentNodeData?.tileBuilding) {
    return null;
  }

  // Calculate distance for cost display
  const coords = nodeId ? idToCoords(nodeId) : null;
  const distance = coords ? hexDistance(0, 0, coords.q, coords.r) : 0;
  const multiplier = distance > 0 ? Math.round((1 + 0.15 * distance) * 100) : 100;

  // Check if can afford route
  const canAffordRoute = routeCost && Object.entries(routeCost).every(
    ([res, amt]) => (playerResources?.[res] || 0) >= amt
  );

  return (
    <div className="wm-action-group">
      <div className="wm-action-label">Built</div>
      <div className="wm-built-info">
        <BuildingChip id={currentNodeData.tileBuilding.id} />
      </div>

      {/* Trade Route Section - only show if tile has a building */}
      <div className="wm-tr-section">
        <div className="wm-tr-header">
          <span className="wm-tr-label">🛤️ Trade Route</span>
          {isOnTradeRoute && (
            <span className="wm-tr-connected">Connected</span>
          )}
        </div>
        {isOnTradeRoute ? (
          <div className="wm-tr-status wm-tr-active">
            Connected to Kingdom
          </div>
        ) : canBuildRoute ? (
          <div className="wm-tr-build-section">
            <div className="wm-tr-cost-row">
              {routeCost && Object.entries(routeCost).map(([res, amt]) => {
                const have = playerResources?.[res] || 0;
                const canAffordThis = have >= amt;
                return (
                  <ResourceChip
                    key={res}
                    resourceId={res}
                    amount={amt}
                    size="small"
                    className={`wm-tr-cost-chip ${!canAffordThis ? 'wm-tr-insufficient' : ''}`}
                  />
                );
              })}
            </div>
            {distance > 1 && (
              <div className="wm-tr-distance">
                {distance} tiles away ({multiplier}% cost)
              </div>
            )}
            <button
              className="wm-tr-build-btn"
              onClick={() => {
                // Trigger guided tour on first trade route build attempt
                if (!completedGuidedTours?.includes('tradeRoutes')) {
                  startGuidedTour?.('tradeRoutes');
                }
                onBuildRoute?.(nodeId);
              }}
              disabled={!canAffordRoute}
            >
              Build Route
            </button>
          </div>
        ) : (
          <div className="wm-tr-status wm-tr-blocked">
            Must be adjacent to existing route
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * TileBuildingPreview - Read-only preview of buildings available on a tile
 * Shows what CAN be built, not interactive build controls
 */
export function TileBuildingPreview({ availableBuildings, buildingCounts }) {
  if (!availableBuildings || availableBuildings.length === 0) {
    return null;
  }

  return (
    <div className="wm-preview-section wm-build-preview">
      <div className="wm-preview-label">🏗️ Can Build Here</div>
      <div className="wm-build-preview-list">
        {availableBuildings.slice(0, 6).map(bld => (
          <div key={bld.id} className="wm-build-preview-item">
            <BuildingChip id={bld.id} size="small" count={buildingCounts?.[bld.id] || 0} />
          </div>
        ))}
        {availableBuildings.length > 6 && (
          <div className="wm-build-preview-more">
            +{availableBuildings.length - 6} more
          </div>
        )}
      </div>
    </div>
  );
}

export default { TileBuildingUI, TileBuildingButton, BuiltTileInfo, TileBuildingPreview };
